import type { VoidFunctionComponent } from 'react';

import Badge from '../../generic/Badge/Badge';
import FromNowWithTooltip from '../../generic/FromNow/FromNowWithTooltip';
import List from '../../generic/List';

import styles from './ActivityFeed.module.less';
import type { LegalAgentActivityActor, LegalAgentActivityEntry } from '../../../../shared/types/routes/legalAgent';

export function actorInitial(actor: LegalAgentActivityActor): string {
  if (actor.type === 'agent') {
    return 'A';
  }
  return actor.name?.trim().charAt(0).toUpperCase() || 'U';
}

export function actorName(actor: LegalAgentActivityActor): string {
  if (actor.type === 'agent') {
    return 'Agent';
  }
  return actor.name?.trim() || 'Unknown user';
}

//  Which project, or else which release groups, the entry is about.
function scopeLabel(entry: LegalAgentActivityEntry): string | null {
  if (entry.project) {
    return entry.project.title ?? entry.project.locator;
  }
  const groups = [...new Set(entry.releases.map((release) => release.releaseGroupTitle))];
  return groups.length > 0 ? groups.join(', ') : null;
}

type Props = {
  entry: LegalAgentActivityEntry;
  //  Off when the scope is exactly one project, where naming it on every row is noise.
  showScope: boolean;
};

export const ActivityFeedRow: VoidFunctionComponent<Props> = ({ entry, showScope }) => {
  const scope = showScope ? scopeLabel(entry) : null;

  return (
    <List.RowContainer>
      <List.Row
        isInteractive={false}
        flexDirection="row"
        testId="legal-agent-activity-row"
        left={<Badge color="light-gray" shape="circle" size="small">{actorInitial(entry.actor)}</Badge>}
        right={<span className={styles.time}><FromNowWithTooltip date={entry.occurredAt} /></span>}
      >
        <div className={styles.body}>
          <span className={styles.title}>{entry.description}</span>
          <span className={styles.meta} data-test-id="legal-agent-activity-row-meta">
            {scope ? `${actorName(entry.actor)} · ${scope}` : actorName(entry.actor)}
          </span>
        </div>
      </List.Row>
    </List.RowContainer>
  );
};
